
import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { eventApi, fightApi } from "../services/api";
import FightCard from "../components/events/FightCard";
import "../components/events/FightCard.css";

const FIGHT_SECTIONS = [
    { key: "MC", title: "Main Card" },
    { key: "P", title: "Prelims" },
    { key: "EP", title: "Early Prelims" },
    { key: "OTHER", title: "Other Fights" },
];

const getFightSectionKey = (fight) => {
    const sectionKey = fight?.card_position?.split(".")[0]?.trim()?.toUpperCase();

    if (sectionKey === "MC" || sectionKey === "P" || sectionKey === "EP") {
        return sectionKey;
    }

    return "OTHER";
};

function FightsView() {
    const { id } = useParams();
    const eventId = Number(id);

    const [fights, setFights] = useState([]);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fightSections = useMemo(() => {
        const sections = FIGHT_SECTIONS.map((section) => ({
            ...section,
            fights: [],
        }));
        const sectionsByKey = Object.fromEntries(sections.map((section) => [section.key, section]));

        fights.forEach((fight) => {
            sectionsByKey[getFightSectionKey(fight)].fights.push(fight);
        });

        return sections.filter((section) => section.fights.length > 0);
    }, [fights]);

    useEffect(() => {
        if (!eventId) {
            setLoading(false);
            return;
        }

        const fetchEventData = async () => {
            try {
                setLoading(true);
                const eventData = await eventApi.getEvent(eventId);

                setEvent(eventData);
                const fightsData = await fightApi.getFightsByEventId(eventId);

                setFights(fightsData);

            } catch (err) {
                setError(err.message || String(err));

                console.error("Error fetching event data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchEventData();
    }, [eventId]);

    if (loading) {
        return (
            <div className="container my-4">
                <div className="text-center">
                    <p>Loading event data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container my-4">
                <div className="alert alert-danger" role="alert">
                    <h4>Error Loading Event</h4>
                    <p>{error}</p>
                    <button 
                        className="btn btn-primary" 
                        onClick={() => window.location.reload()}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="container my-4">
                <div className="alert alert-warning">
                    No event found with this ID.
                </div>
            </div>
        );
    }

    return (
        <div className="container my-4 fights-view">
            <div className="card mb-4 fights-event-card">
                <div className="card-body fights-card-body">
                    <h2 className="card-title mb-1 fight-card-title">{event.name}</h2>
                    <p className="card-text mb-0">
                        {new Date(event.date_str).toLocaleDateString()} - {event.location}
                    </p>
                </div>
            </div>            
            <div className="fights-grid">
                {fightSections.map((section) => (
                    <section className="fight-section" key={section.key} aria-labelledby={`fight-section-${section.key}`}>
                        <div className="fight-section-header">
                            <h3 className="fight-section-title" id={`fight-section-${section.key}`}>
                                {section.title}
                            </h3>
                            <span className="fight-section-count">{section.fights.length}</span>
                        </div>
                        {section.fights.map((fight) => (
                            <FightCard fight={fight} key={fight.fight_id} />
                        ))}
                    </section>
                ))}
            </div>
        </div>
    );
}

export default FightsView;
